import React, { useCallback, useEffect, useRef, useState } from 'react';

const FADE_MS = 300;
const VIEWPORT_MARGIN = 8;
const DRAG_THRESHOLD = 5;

function clampPosition(x, y, width, height) {
  return {
    x: Math.min(Math.max(VIEWPORT_MARGIN, x), window.innerWidth - width - VIEWPORT_MARGIN),
    y: Math.min(Math.max(VIEWPORT_MARGIN, y), window.innerHeight - height - VIEWPORT_MARGIN)
  };
}

function formatDisplay(value) {
  if (!Number.isFinite(value)) return 'Error';
  const abs = Math.abs(value);
  if (abs !== 0 && (abs >= 1e12 || abs < 1e-9)) {
    return value.toExponential(6);
  }
  const text = String(Number(value.toPrecision(12)));
  return text.length > 14 ? value.toExponential(6) : text;
}

function applyBinary(a, op, b) {
  switch (op) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '×':
      return a * b;
    case '÷':
      return b === 0 ? NaN : a / b;
    default:
      return b;
  }
}

/**
 * Floating on-screen calculator for the Quantitative Reasoning chapter.
 * Fades in/out with `open`; uses theme surface tokens for light and dark mode.
 */
function FloatingCalculator({ open, t }) {
  const [rendered, setRendered] = useState(open);
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const containerRef = useRef(null);
  const collapsedRef = useRef(false);
  const blockExpandClickRef = useRef(false);
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0
  });
  const [position, setPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [display, setDisplay] = useState('0');
  const [accumulator, setAccumulator] = useState(null);
  const [pendingOp, setPendingOp] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const reset = useCallback(() => {
    setDisplay('0');
    setAccumulator(null);
    setPendingOp(null);
    setWaitingForOperand(false);
  }, []);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    const timeout = window.setTimeout(() => {
      setRendered(false);
      reset();
      setCollapsed(false);
      setPosition(null);
      setIsDragging(false);
    }, FADE_MS);
    return () => window.clearTimeout(timeout);
  }, [open, reset]);

  useEffect(() => {
    collapsedRef.current = collapsed;
  }, [collapsed]);

  const getCurrentPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    return { x: rect.left, y: rect.top };
  }, []);

  const reclampPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el || !position) return;
    setPosition((prev) => {
      if (!prev) return prev;
      return clampPosition(prev.x, prev.y, el.offsetWidth, el.offsetHeight);
    });
  }, [position]);

  useEffect(() => {
    if (!position) return undefined;
    const onResize = () => reclampPosition();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position, reclampPosition]);

  useEffect(() => {
    if (!position) return;
    reclampPosition();
  }, [collapsed, position, reclampPosition]);

  const handleDragStart = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const current = position ?? getCurrentPosition();
    if (!position) setPosition(current);
    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      originX: current.x,
      originY: current.y
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event) => {
    if (!dragRef.current.active) return;
    const el = containerRef.current;
    if (!el) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    if (!dragRef.current.moved && Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
      dragRef.current.moved = true;
    }
    setPosition(
      clampPosition(
        dragRef.current.originX + dx,
        dragRef.current.originY + dy,
        el.offsetWidth,
        el.offsetHeight
      )
    );
  };

  const handleDragEnd = (event) => {
    if (!dragRef.current.active) return;
    const wasMoved = dragRef.current.moved;
    dragRef.current.active = false;
    dragRef.current.moved = false;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (collapsedRef.current && !wasMoved) {
      setCollapsed(false);
    } else if (wasMoved) {
      blockExpandClickRef.current = true;
    }
  };

  const handleCollapsedClick = () => {
    if (blockExpandClickRef.current) {
      blockExpandClickRef.current = false;
      return;
    }
    setCollapsed(false);
  };

  const inputDigit = (digit) => {
    setDisplay((prev) => {
      if (waitingForOperand || prev === 'Error') {
        setWaitingForOperand(false);
        return digit;
      }
      if (prev === '0') return digit;
      if (prev === '-0') return `-${digit}`;
      if (prev.replace('-', '').length >= 12) return prev;
      return prev + digit;
    });
  };

  const inputDot = () => {
    setDisplay((prev) => {
      if (waitingForOperand || prev === 'Error') {
        setWaitingForOperand(false);
        return '0.';
      }
      if (prev.includes('.')) return prev;
      return `${prev}.`;
    });
  };

  const commitPending = (nextValue) => {
    if (accumulator !== null && pendingOp && !waitingForOperand) {
      const result = applyBinary(accumulator, pendingOp, nextValue);
      const text = formatDisplay(result);
      setDisplay(text);
      setAccumulator(Number.isFinite(result) ? result : null);
      return result;
    }
    setAccumulator(nextValue);
    return nextValue;
  };

  const chooseOperator = (op) => {
    const current = display === 'Error' ? NaN : Number(display);
    if (!Number.isFinite(current)) {
      reset();
      return;
    }
    if (pendingOp && !waitingForOperand) {
      const result = commitPending(current);
      if (!Number.isFinite(result)) {
        setPendingOp(null);
        setWaitingForOperand(false);
        return;
      }
    } else {
      setAccumulator(current);
    }
    setPendingOp(op);
    setWaitingForOperand(true);
  };

  const equals = () => {
    const current = display === 'Error' ? NaN : Number(display);
    if (!Number.isFinite(current) || accumulator === null || !pendingOp) return;
    const result = applyBinary(accumulator, pendingOp, current);
    setDisplay(formatDisplay(result));
    setAccumulator(null);
    setPendingOp(null);
    setWaitingForOperand(true);
  };

  const square = () => {
    const current = display === 'Error' ? NaN : Number(display);
    if (!Number.isFinite(current)) {
      setDisplay('Error');
      return;
    }
    setDisplay(formatDisplay(current * current));
    setWaitingForOperand(true);
  };

  const squareRoot = () => {
    const current = display === 'Error' ? NaN : Number(display);
    if (!Number.isFinite(current) || current < 0) {
      setDisplay('Error');
      setAccumulator(null);
      setPendingOp(null);
      setWaitingForOperand(true);
      return;
    }
    setDisplay(formatDisplay(Math.sqrt(current)));
    setWaitingForOperand(true);
  };

  if (!rendered) return null;

  const keyClass =
    'h-10 rounded-lg border border-edge bg-surface-muted text-ink text-sm font-semibold ' +
    'hover:bg-edge active:scale-[0.97] transition-colors focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface';

  const opKeyClass =
    'h-10 rounded-lg border border-edge bg-primary/10 text-primary text-sm font-semibold ' +
    'hover:bg-primary/20 active:scale-[0.97] transition-colors focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface';

  const keys = [
    { label: 'C', onClick: reset, className: opKeyClass, aria: t('test.calculatorClear') },
    { label: '√', onClick: squareRoot, className: opKeyClass, aria: t('test.calculatorSqrt') },
    { label: 'x²', onClick: square, className: opKeyClass, aria: t('test.calculatorSquare') },
    { label: '÷', onClick: () => chooseOperator('÷'), className: opKeyClass, aria: t('test.calculatorDivide') },
    { label: '7', onClick: () => inputDigit('7'), className: keyClass },
    { label: '8', onClick: () => inputDigit('8'), className: keyClass },
    { label: '9', onClick: () => inputDigit('9'), className: keyClass },
    { label: '×', onClick: () => chooseOperator('×'), className: opKeyClass, aria: t('test.calculatorMultiply') },
    { label: '4', onClick: () => inputDigit('4'), className: keyClass },
    { label: '5', onClick: () => inputDigit('5'), className: keyClass },
    { label: '6', onClick: () => inputDigit('6'), className: keyClass },
    { label: '−', onClick: () => chooseOperator('-'), className: opKeyClass, aria: t('test.calculatorSubtract') },
    { label: '1', onClick: () => inputDigit('1'), className: keyClass },
    { label: '2', onClick: () => inputDigit('2'), className: keyClass },
    { label: '3', onClick: () => inputDigit('3'), className: keyClass },
    { label: '+', onClick: () => chooseOperator('+'), className: opKeyClass, aria: t('test.calculatorAdd') },
    { label: '0', onClick: () => inputDigit('0'), className: `${keyClass} col-span-2` },
    { label: '.', onClick: inputDot, className: keyClass },
    { label: '=', onClick: equals, className: opKeyClass, aria: t('test.calculatorEquals') }
  ];

  const placementClass = position ? '' : 'bottom-4 right-4 md:bottom-6 md:right-6';
  const placementStyle = position ? { left: position.x, top: position.y } : undefined;

  return (
    <div
      ref={containerRef}
      className={`fixed z-40 ${placementClass} transition-opacity duration-300 ease-out ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      } ${isDragging ? 'select-none' : ''}`}
      style={placementStyle}
      role="region"
      aria-label={t('test.calculatorTitle')}
      aria-hidden={!visible}
    >
      {collapsed ? (
        <button
          type="button"
          onClick={handleCollapsedClick}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          className={`flex h-12 w-12 items-center justify-center rounded-full border border-edge bg-surface text-ink shadow-lg hover:bg-surface-muted touch-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          aria-label={t('test.calculatorExpand')}
          title={t('test.calculatorTitle')}
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
            <path d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm1 3v3h8V6H8zm0 5v2h2v-2H8zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2zM8 15v2h2v-2H8zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2z" />
          </svg>
        </button>
      ) : (
        <div className="w-[220px] rounded-xl border border-edge bg-surface shadow-lg overflow-hidden">
          <div
            className={`flex items-center justify-between gap-2 border-b border-edge px-3 py-2 bg-surface-muted touch-none ${
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
          >
            <span className="text-xs font-semibold text-ink-muted tracking-wide pointer-events-none">
              {t('test.calculatorTitle')}
            </span>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              onPointerDown={(event) => event.stopPropagation()}
              className="text-ink-subtle hover:text-ink text-sm leading-none px-1.5 py-0.5 rounded cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('test.calculatorCollapse')}
            >
              −
            </button>
          </div>
          <div className="px-3 pt-3 pb-2">
            <div
              className="rounded-lg border border-edge bg-canvas px-3 py-2 text-right font-mono text-lg text-ink tabular-nums truncate"
              aria-live="polite"
            >
              {display}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5 p-3 pt-1">
            {keys.map((key) => (
              <button
                key={key.label}
                type="button"
                onClick={key.onClick}
                className={key.className}
                aria-label={key.aria || key.label}
              >
                {key.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FloatingCalculator;
